import React, {
    useState,
    useEffect,
    createContext,
    useMemo,
    lazy,
    Suspense
} from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    Outlet
} from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { HelmetProvider } from 'react-helmet-async';

// CONTEXT PROVIDERS
import I18nProvider from './context/I18nContext';
import TelegramProvider from './context/TelegramContext';
import { ConfigProvider } from './context/ConfigContext';

// COMPONENTS
import BottomNavigation from './components/BottomNavigation';
import DeepLinkHandler from './components/DeepLinkHandler';

// LAZY-LOADED PAGES
const Home = lazy(() => import('./pages/Home'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Ads = lazy(() => import('./pages/Ads'));
const Messages = lazy(() => import('./pages/Messages'));
const Profile = lazy(() => import('./pages/Profile'));
const AdDetails = lazy(() => import('./pages/AdDetails'));
const AdStats = lazy(() => import('./pages/AdStats'));
const AdForm = lazy(() => import('./pages/AdForm'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Moderation = lazy(() => import('./pages/Moderation'));
const FilterForm = lazy(() => import('./pages/FilterForm'));

// --- МОДЕРНИЗИРОВАННАЯ СТРУКТУРА ---

// 1. Централизованное управление темой (остается без изменений)
export const ThemeContext = createContext({
    themeMode: 'system',
    setThemeMode: () => {},
});

const useAppTheme = () => {
    const [themeMode, setThemeMode] = useState(() => {
        return localStorage.getItem('themeMode') || 'system';
    });
    const tg = window.Telegram?.WebApp;

    useEffect(() => {
        localStorage.setItem('themeMode', themeMode);
    }, [themeMode]);

    useEffect(() => {
        if (!tg || themeMode !== 'system') return;
        const handleThemeChange = () => setThemeMode('system');
        tg.onEvent('themeChanged', handleThemeChange);
        return () => tg.offEvent('themeChanged', handleThemeChange);
    }, [themeMode, tg]);

    const theme = useMemo(() => {
        const getActualMode = () => {
            if (themeMode === 'system') {
                return tg?.colorScheme === 'dark' || window.matchMedia('(prefers-color-scheme: dark)').matches
                    ? 'dark'
                    : 'light';
            }
            return themeMode;
        };
        const actualMode = getActualMode();

        return createTheme({
            palette: {
                mode: actualMode,
                primary: { main: '#8a00ff' },
                background: {
                    default: actualMode === 'dark' ? '#121212' : '#f5f5f5',
                    paper: actualMode === 'dark' ? '#1e1e1e' : '#ffffff',
                },
                text: {
                    primary: actualMode === 'dark' ? '#ffffff' : '#000000',
                    secondary: actualMode === 'dark' ? '#aaaaaa' : '#8e8e93',
                },
            },
        });
    }, [themeMode, tg?.colorScheme]);

    return { theme, themeMode, setThemeMode };
};

// 2. Централизованные провайдеры (остается без изменений)
const AppProviders = ({ children }) => {
    const { theme, themeMode, setThemeMode } = useAppTheme();
    const themeContextValue = useMemo(() => ({ themeMode, setThemeMode }), [themeMode, setThemeMode]);

    return (
        <HelmetProvider>
            <ConfigProvider>
                <ThemeContext.Provider value={themeContextValue}>
                    <I18nProvider>
                        <ThemeProvider theme={theme}>
                            <CssBaseline />
                            {children}
                        </ThemeProvider>
                    </I18nProvider>
                </ThemeContext.Provider>
            </ConfigProvider>
        </HelmetProvider>
    );
};

// 3. Основной макет приложения
const MainLayout = () => (
    <TelegramProvider>
        <DeepLinkHandler />
        <Box sx={{ pb: 7, minHeight: '100vh' }}>
            <Outlet /> {/* Дочерние маршруты будут отображаться здесь */}
        </Box>
        <BottomNavigation />
    </TelegramProvider>
);

// 4. Компонент для отображения во время загрузки
const LoadingFallback = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
    </Box>
);

// 5. Главный компонент App (чистый и декларативный)
function App() {
    return (
        <AppProviders>
            <Router>
                <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                        {/* Основные маршруты приложения с общим макетом и навигацией */}
                        <Route path="/" element={<MainLayout />}>
                            <Route index element={<Home />} />
                            <Route path="favorites" element={<Favorites />} />
                            <Route path="ads" element={<Ads />} />
                            <Route path="messages" element={<Messages />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="ad/:id" element={<AdDetails />} />
                            <Route path="ad/:id/stats" element={<AdStats />} />
                            <Route path="create-ad" element={<AdForm />} />
                            <Route path="edit-ad/:id" element={<AdForm />} />
                            <Route path="transactions" element={<Transactions />} />
                            <Route path="moderation" element={<Moderation />} />
                            <Route path="selection/:type" element={<FilterForm />} />
                        </Route>

                        {/* Перенаправление всех остальных неизвестных путей на главную страницу */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Suspense>
            </Router>
        </AppProviders>
    );
}

export default App;
